/**
 * The lesson card: the five verbs — build, wall, muster, command, cast — said
 * in words, over five waves that were bent to teach them.
 *
 * **The simulation is the tutorial.** The bend lives in `sim/systems/tutorial.ts`
 * and rides on `World.tutorialStep`; this file is a *reader* over it, exactly as
 * `codex.ts` is a reader over the defs. Nothing here writes the world except the
 * one command the Skip link sends, no lesson gates anything, and the card advances
 * because a **wave ended**, never because a card said so. Closing it leaves an
 * ordinary run behind, at whatever wave it had reached, because a guided wave was
 * only ever an ordinary wave with its parameters bent.
 *
 * The one editorial rule is the Codex's and it is inherited on purpose: the card
 * only ever describes what the simulation actually reads. The gate it names is
 * the gate out of `scoutingReport`, so it is the gate the minimap is already
 * pulsing and the gate the horde will actually walk in from; the sentence about
 * what the wave is made of is the lesson's own `brief`, and the shape it
 * describes is the shape `shapeGuidedWave` builds. A tutorial that taught a verb
 * the game does not have would be the same lie the Codex refuses to print, only
 * louder.
 *
 * `TUTORIAL_STEPS` is data — id, title, instruction, and a `done` predicate that
 * is a pure read of the world — so `tests/tutorial.test.ts` holds every predicate
 * against a constructed world with no DOM in sight, and plays the whole run
 * through the command vocabulary.
 */

import { BUILDING_TYPES, buildingTypeById } from '../data/buildings.ts';
import { unitTypeById } from '../data/units.ts';
import { Phase, Team } from '../sim/types.ts';
import { OrderKind, statsOf, type World } from '../sim/world.ts';
import { wallTileCount } from '../sim/bots.ts';
import { scoutingReport } from '../sim/systems/director.ts';
import { tutorialLessons, TUTORIAL_WAVES, type LessonId } from '../sim/systems/tutorial.ts';
import { Cmd, cmd, type Command } from '../sim/commands.ts';
import { KEY_FORMATION, KEY_HORN, KEY_LEDGER, keyLabel, POWER_KEY_LABELS } from './keys.ts';

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string);
}

/** Player buildings of a given type id, finished or not — placing is the lesson. */
function countOf(w: World, id: string): number {
  let n = 0;
  const b = w.buildings;
  for (let i = 0; i < b.slots.high; i++) {
    if (b.slots.alive[i] === 0 || b.team[i] !== Team.Player) continue;
    if (BUILDING_TYPES[b.typeId[i]].id === id) n++;
  }
  return n;
}

export interface TutorialStep {
  /** The same id the simulation's lesson carries. One vocabulary, not two. */
  id: LessonId;
  title: string;
  /** Instruction HTML. Keys are interpolated from `keys.ts`, never retyped. */
  body: string;
  /** True when the lesson has been learned. Pure read of the world. */
  done: (w: World) => boolean;
  /** What the player has to show for it, printed once `done`. */
  earned: string;
}

/** Data-quoted names, so the card can never disagree with the button. */
const bName = (id: string): string => esc(buildingTypeById(id).name);

export const TUTORIAL_STEPS: TutorialStep[] = [
  {
    id: 'build',
    title: 'Build',
    body:
      `Pan with <b>W A S D</b>, zoom with the <b>wheel</b>. Everything else costs timber and grain. ` +
      `From the build list on the left, place a <b>${bName('woodcutter')}</b> near trees and a ` +
      `<b>${bName('farm')}</b> on open ground — pick the building, then click the map. ` +
      `Villagers walk out and raise it themselves; nobody has to be told to work.`,
    done: (w) => countOf(w, 'woodcutter') >= 1 && countOf(w, 'farm') >= 1,
    earned: 'Timber and grain now come in on their own. Every other lesson is paid for out of that.',
  },
  {
    id: 'wall',
    title: 'Wall',
    body:
      `The <b>scouting panel</b> names the gate they are coming from, and the minimap is pulsing it. ` +
      `Under <b>The Works</b> in the build list, pick <b>Wall</b> and <b>drag</b> a line of at ` +
      `least eight stones across that approach — then leave a <b>Gate</b> in it, or your own ` +
      `people are walled out of their own fields.`,
    done: (w) => wallTileCount(w) >= 8,
    earned: 'Stone across the road they use. This wave has nothing in it that can go over or under.',
  },
  {
    id: 'muster',
    title: 'Muster',
    body:
      `A settlement without soldiers is a larder, and this is the lesson that takes the longest — ` +
      `so this build phase is the longest in the run. Place <b>${bName('house')}</b> for the beds and ` +
      `<b>${bName('barracks')}</b> for the drill, wait for the yard to <b>stand</b>, then ` +
      `<b>click it</b> and drill <b>${esc(unitTypeById('ashen_militia').name)}</b> from its panel. ` +
      `The muster takes grown people out of your workforce and puts them in the roll — if it comes ` +
      `up short, the refusal says which of the four it was short of.`,
    done: (w) => w.companies.some((c) => c.team === Team.Player && !c.dead && c.members.length > 0),
    earned: 'A company on the field. Fewer hands in the fields — that trade is the whole game.',
  },
  {
    id: 'command',
    title: 'Command',
    body:
      `<b>Left-click</b> your company, or drag a box around it, then <b>right-click</b> where it ` +
      `should stand. <b>Right-drag</b> sets a frontage and a facing; ` +
      `<b>${esc(keyLabel(KEY_FORMATION))}</b> cycles formation. ` +
      `Do it now, because this wave comes in at <b>two</b> gates and only one of them is yours.`,
    done: (w) => w.companies.some((c) => c.team === Team.Player && c.orderKind !== OrderKind.None),
    earned: 'They go where they are sent. A company left where it was raised only ever holds one gate.',
  },
  {
    id: 'cast',
    title: 'Cast',
    body:
      `You are not only a planner. The seals along the bottom are <b>Sovereign Powers</b>, paid in ` +
      `Will — press <b>${esc(POWER_KEY_LABELS[0])}</b> (or click the first seal) and click the ` +
      `ground. Will refills by itself; the cooldown is the real price, so spend it on a crowd.`,
    done: (w) => statsOf(w).powersCast >= 1,
    earned: 'Will spent well. They arrive in one press this wave, which is exactly what a Power is for.',
  },
];

export const TUTORIAL_FAREWELL = {
  title: 'The rest is yours',
  body:
    `That is the whole loop: build, wall, muster, command, cast — then rebuild what the siege cost ` +
    `and go again, a little deeper each time. From here the waves are the ordinary ones: they come ` +
    `from more gates, they carry sappers and fliers and weather, and nothing is bent for you. ` +
    `The <b>Ledger</b> (<b>${esc(keyLabel(KEY_LEDGER))}</b>) counts everything; the <b>Codex</b> on ` +
    `the title screen holds every rule. This run keeps going as long as you can hold it.`,
};

// ── Progress, on its own browser key ────────────────────────────────────────

/**
 * Whether this *player* has been taught, as against whether this *run* is being
 * taught.
 *
 * Deliberately **not** in the world save, on the bloodline's argument: the save
 * is a run, and "I have done the lessons" outlives every run — a player who
 * deletes a dead settlement has not forgotten how to lay a wall. `tutorialStep`
 * is the run's business and is saved with the run; this is the person's, and it
 * lives on its own key beside `hearth.bloodline.v1`, `hearth.audio.v1` and
 * `hearth.options.v1`. Nothing in the simulation reads it.
 */
export const TUTORIAL_KEY = 'hearth.tutorial.v1';

export interface TutorialProgress {
  v: 1;
  /** The guided run has been opened at least once. */
  seen: boolean;
  /** The furthest lesson reached, 0–5. */
  furthest: number;
  /** All five waves survived with the lessons still on. */
  finished: boolean;
}

interface KV {
  getItem(k: string): string | null;
  setItem(k: string, v: string): void;
}

function emptyProgress(): TutorialProgress {
  return { v: 1, seen: false, furthest: 0, finished: false };
}

/**
 * Tolerant on the bloodline's pattern: a garbled record reads as an untaught
 * player rather than throwing on the title screen, and every field is bounded
 * on the way in so a hand-edited key cannot print "lesson 900 of 5".
 */
export class TutorialStore {
  private p: TutorialProgress;

  constructor(private kv: KV, private key: string = TUTORIAL_KEY) {
    this.p = this.read();
  }

  private read(): TutorialProgress {
    let raw: unknown;
    try {
      const json = this.kv.getItem(this.key);
      if (!json) return emptyProgress();
      raw = JSON.parse(json);
    } catch {
      return emptyProgress();
    }
    if (typeof raw !== 'object' || raw === null) return emptyProgress();
    const b = raw as Partial<TutorialProgress>;
    const n = typeof b.furthest === 'number' && Number.isFinite(b.furthest) ? b.furthest : 0;
    return {
      v: 1,
      seen: b.seen === true,
      furthest: Math.max(0, Math.min(TUTORIAL_WAVES, Math.floor(n))),
      finished: b.finished === true,
    };
  }

  private write(): void {
    try {
      this.kv.setItem(this.key, JSON.stringify(this.p));
    } catch {
      // Quota, or storage switched off. The session still remembers, which is
      // the most that can honestly be offered.
    }
  }

  state(): TutorialProgress {
    return { ...this.p };
  }

  /**
   * Record where a guided run has got to. `step` is `World.tutorialStep`, so
   * `TUTORIAL_WAVES + 1` means the fifth wave was survived. Monotonic: a second,
   * abandoned attempt cannot un-teach the first.
   */
  note(step: number): void {
    if (!Number.isFinite(step) || step < 1) return;
    const before = JSON.stringify(this.p);
    this.p.seen = true;
    this.p.furthest = Math.max(this.p.furthest, Math.min(TUTORIAL_WAVES, Math.floor(step)));
    if (step > TUTORIAL_WAVES) this.p.finished = true;
    if (JSON.stringify(this.p) !== before) this.write();
  }
}

let store: TutorialStore | null = null;

/** Built lazily: this module is imported by tests running in Node. */
export function tutorialProgress(): TutorialStore {
  if (!store) {
    const forgetful: KV = (() => {
      let held: string | null = null;
      return { getItem: () => held, setItem: (_k, v) => void (held = v) };
    })();
    store = new TutorialStore(typeof localStorage !== 'undefined' ? localStorage : forgetful);
  }
  return store;
}

/** One line for the menu row, or '' for a player who has never opened it. */
export function tutorialMenuNote(p: TutorialProgress): string {
  if (p.finished) return 'You have been through all five.';
  if (p.furthest > 0) return `You reached lesson ${p.furthest} of ${TUTORIAL_WAVES}.`;
  return '';
}

// ── The card ────────────────────────────────────────────────────────────────

export class Tutorial {
  private el: HTMLElement | null = null;
  private styled = false;
  /** What is currently drawn, so the card is not rebuilt sixty times a second. */
  private shown = '';

  constructor(private cb: { onCommand(c: Command): void } = { onCommand: () => {} }) {}

  get active(): boolean {
    return this.el !== null;
  }

  /**
   * Once a frame, over whatever world is on screen.
   *
   * Everything it needs is in the world, which is what makes a guided run
   * *resumable*: a save taken between lesson three and lesson four loads with
   * `tutorialStep` at 4 and the card comes back on its own, on the right lesson,
   * with no UI state to restore.
   */
  update(w: World): void {
    const step = w.tutorialStep;
    if (step < 1 || w.gameOver) {
      this.close();
      return;
    }
    tutorialProgress().note(step);
    const idx = Math.min(step, TUTORIAL_WAVES + 1) - 1;
    const lesson = TUTORIAL_STEPS[idx];
    const learned = lesson ? lesson.done(w) : true;
    const key = `${step}|${learned ? 1 : 0}|${w.phase}|${w.incomingGates.join(',')}`;
    if (key === this.shown) return;
    this.shown = key;
    this.render(w, idx, learned);
  }

  /** Hide the card. Never writes the world — see `skip` for that. */
  close(): void {
    this.el?.remove();
    this.el = null;
    this.shown = '';
  }

  /** Kept for `main.ts`, which closes the card when it leaves for another screen. */
  stop(): void {
    this.close();
  }

  private skip(): void {
    this.cb.onCommand(cmd(Cmd.TutorialSkip));
    this.close();
  }

  private injectStyle(): void {
    if (this.styled) return;
    this.styled = true;
    const s = document.createElement('style');
    s.textContent = `
      /* Right of the scouting panel, left of the companies rail — the card
         must cover neither: scouting is the other thing a new player should
         be reading, and lesson two sends them to it by name. */
      #tutorial { position: absolute; top: 58px; right: 290px;
        z-index: 55; width: min(520px, 55vw); padding: 13px 16px 11px;
        background: rgba(16,14,12,0.94); border: 1px solid var(--gold-deep);
        clip-path: polygon(10px 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%, 0 10px);
        pointer-events: auto; }
      #tutorial .tu-step { font-size: 9.5px; letter-spacing: 0.24em; text-transform: uppercase;
        color: var(--gold-deep); margin-bottom: 4px; }
      #tutorial .tu-title { font-family: 'Cinzel', Georgia, serif; font-size: 15px;
        letter-spacing: 0.08em; color: var(--gold-pale); margin-bottom: 6px; }
      #tutorial .tu-body { font-size: 12px; color: var(--bone-dim); line-height: 1.6; }
      #tutorial .tu-body b { color: var(--bone); }
      #tutorial .tu-wave { font-size: 11px; color: var(--bone-deep); line-height: 1.55;
        margin-top: 8px; padding-top: 7px; border-top: 1px solid rgba(200,164,76,0.16); }
      #tutorial .tu-wave b { color: var(--gold-pale); }
      #tutorial .tu-row { display: flex; align-items: center; justify-content: space-between;
        margin-top: 9px; }
      #tutorial .tu-skip { font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase;
        color: var(--bone-deep); cursor: pointer; }
      #tutorial .tu-skip:hover { color: var(--bone); }
      #tutorial .tu-next { font-size: 11px; padding: 5px 14px; cursor: pointer;
        color: var(--gold-pale); border: 1px solid var(--gold-deep);
        background: rgba(200,164,76,0.12);
        clip-path: polygon(5px 0, 100% 0, calc(100% - 5px) 100%, 0 100%); }
      #tutorial .tu-next:hover { background: rgba(200,164,76,0.22); }
      #tutorial .tu-wait { font-size: 10px; font-style: italic; color: var(--bone-deep); }
    `;
    document.head.appendChild(s);
  }

  private render(w: World, idx: number, learned: boolean): void {
    this.injectStyle();
    if (!this.el) {
      this.el = document.createElement('div');
      this.el.id = 'tutorial';
      (document.getElementById('ui-root') as HTMLElement).appendChild(this.el);
    }

    const lesson: TutorialStep | undefined = TUTORIAL_STEPS[idx];
    if (!lesson) {
      // Graduated. One button, and it only closes a card.
      this.el.innerHTML =
        `<div class="tu-step">Lessons done</div>` +
        `<div class="tu-title">${esc(TUTORIAL_FAREWELL.title)}</div>` +
        `<div class="tu-body">${TUTORIAL_FAREWELL.body}</div>` +
        `<div class="tu-row"><span class="tu-next" id="tu-next">Take the field</span>` +
        `<span class="tu-skip" id="tu-skip">Close</span></div>`;
      (this.el.querySelector('#tu-next') as HTMLElement).onclick = () => this.skip();
      (this.el.querySelector('#tu-skip') as HTMLElement).onclick = () => this.skip();
      return;
    }

    this.el.innerHTML =
      `<div class="tu-step">Lesson ${idx + 1} of ${TUTORIAL_WAVES}</div>` +
      `<div class="tu-title">${esc(lesson.title)}</div>` +
      `<div class="tu-body">${learned ? esc(lesson.earned) : lesson.body}</div>` +
      `<div class="tu-wave">${this.waveLine(w, idx)}</div>` +
      `<div class="tu-row"><span class="tu-wait">${
        w.phase === Phase.Siege ? 'They are here.' : `The horn is <b>${esc(keyLabel(KEY_HORN))}</b>, or wait for the clock.`
      }</span><span class="tu-skip" id="tu-skip">Skip the lessons</span></div>`;
    (this.el.querySelector('#tu-skip') as HTMLElement).onclick = () => this.skip();
  }

  /**
   * What is coming, quoted from the director's own report rather than written
   * out here — so the gate the card names is the gate the minimap is pulsing
   * and the gate they actually walk in from.
   */
  private waveLine(w: World, idx: number): string {
    const sim = tutorialLessons()[idx];
    const gates = scoutingReport(w).gates.map((g) => `<b>${esc(g)}</b>`);
    // Escape each name and *then* join with the markup. Joining first and
    // escaping the result escapes the tags as well, and the card prints its own
    // `</b>` at the player — which is what the shot showed the first time.
    const where = gates.length > 0 ? gates.join(' and ') : '<b>the rim</b>';
    const verb = w.phase === Phase.Siege ? 'They came in at' : 'They come in at';
    return `${verb} ${where}. ${esc(sim ? sim.brief : '')}`;
  }
}
